import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@bambi/component-library/src/components/Button/Button';
import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import {
  TagRead,
  useTagsListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormErrorMessage } from 'src/common/FormErrorMessage';
import { Modal, ModalProps } from 'src/common/primitives/Modal';
import { MultiSelect } from 'src/common/primitives/MultiSelect/MultiSelect';
import { useUserRoles } from 'src/features/auth/useUserRoles';

import type { SelectOptionWithColor } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

type TagModalProps = {
  initialTagIds?: string[];
  modalProps: Omit<ModalProps, 'children'>;
  header: string;
  isExternalLoading: boolean;
  externalError?: string | null;
  handleSaveTags: (
    selectedTags: SelectOptionWithColor[],
    allTags: TagRead[]
  ) => Promise<unknown>;
};

export function TagModal({
  initialTagIds = [],
  modalProps,
  header,
  isExternalLoading,
  externalError,
  handleSaveTags,
}: TagModalProps) {
  const [selectedTags, setSelectedTags] = useState<SelectOptionWithColor[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { isAssociate, isRideOrderingPortalUser } = useUserRoles();
  const { data: tags } = useTagsListQuery(
    {},
    {
      skip: !modalProps.open || isAssociate || isRideOrderingPortalUser,
    }
  );

  const options: SelectOptionWithColor[] = useMemo(() => {
    return (
      tags?.results
        .filter((tag) => !initialTagIds.includes(tag.id))
        .map((tag) => ({
          label: tag.tag_name,
          value: tag.id,
          color: tag.color_hex || '',
        })) || []
    );
  }, [initialTagIds, tags?.results]);

  useEffect(() => {
    if (!modalProps.open) {
      setSelectedTags([]);
      setError(null);
    }
  }, [modalProps.open]);

  const handleSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);

    if (!selectedTags.length) {
      setError('Please select at least one tag');
      setLoading(false);
      return;
    }

    try {
      await handleSaveTags(selectedTags, tags?.results || []);
      setSelectedTags([]);
    } catch (e) {
      setError('Error Saving Tags');
    } finally {
      setLoading(false);
    }
  }, [handleSaveTags, selectedTags, tags?.results]);

  return (
    <Modal
      contentClassnames="max-w-2xl md:min-w-[400px]"
      {...modalProps}
      title={header}
    >
      <h1 className="mb-4 text-2xl" data-testid="tag-modal-header">
        {header}
      </h1>
      {externalError && (
        <div className="mb-4 max-w-[550px]">
          <FormErrorMessage>{externalError}</FormErrorMessage>
        </div>
      )}
      {error && (
        <div className="mb-4 max-w-[550px]">
          <FormErrorMessage>{error}</FormErrorMessage>
        </div>
      )}
      {isExternalLoading && (
        <div className="flex justify-center">
          <LoadingIndicator />
        </div>
      )}
      <MultiSelect
        options={options}
        selected={selectedTags}
        onChange={(selected) => {
          setSelectedTags(selected as SelectOptionWithColor[]);
        }}
        disabled={loading || isExternalLoading}
      />
      <div className="grid grid-cols-2 gap-4 pt-6">
        <Button onClick={() => modalProps.setOpen(false)}>Cancel</Button>
        <Button
          loading={loading || isExternalLoading}
          variant="primary"
          onClick={() => handleSubmit()}
        >
          {loading ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </Modal>
  );
}
